import React from 'react';

import RelativeTime from 'components/common/RelativeTime';
import Customer from 'models/CustomerModel';
import Price from 'components/common/Price';
import { Link } from 'react-router-dom';
import CustomerFullName from 'components/common/CustomerFullName';

interface Props {
    customer: Customer;
}

const CustomerStats: React.FC<Props> = ({ customer }: Props) => {
    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {!!customer && !!customer.stats && (
                <>
                    <div className="border-b border-gray-200 bg-gray-50">
                        <h2 className="px-4 py-3 text-lg font-semibold uppercase tracking-wide text-gray-800">
                            Quick Look
                        </h2>
                    </div>
                    <div className="p-4">
                        <div className="mb-4 px-2 py-3 bg-blue-50 border border-blue-100 rounded-md">
                            <span className="text-gray-800">
                                <span className="font-semibold">
                                    <CustomerFullName customerId={customer.id} customer={customer} />
                                </span>{' '}
                                has visited{' '}
                                <span className="font-semibold">{customer.stats.completedRepairOrdersCount}</span>{' '}
                                times* spending{' '}
                                <span className="text-green-600 font-semibold">
                                    <Price price={customer.stats.lifetimeSpend} />
                                </span>
                            </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                <div className="text-gray-500 text-sm mb-1">Last visit</div>
                                <div className="font-semibold text-lg text-gray-900">
                                    <div>
                                        <RelativeTime dateTime={customer.stats.lastRoDate!} floatRight={false} />
                                    </div>
                                    <div>
                                        <Link
                                            to={`/repairorder/${customer.stats.lastCompletedRO?.id}`}
                                            className="text-blue-500 hover:text-blue-700 hover:underline "
                                            target="_blank"
                                        >
                                            <small>
                                                #{customer.stats.lastCompletedRO?.repairOrderNumber}{' '}
                                                {customer.stats.lastCompletedRO?.title}
                                            </small>
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {customer.stats.lastCompletedRO && (
                                <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                    <div className="text-gray-500 text-sm mb-1">Last RO total</div>
                                    <div className="font-semibold text-lg text-gray-900">
                                        <Price
                                            price={customer.stats.lastCompletedRO.services.reduce(
                                                (acc, service) => acc + service.grandTotalCents,
                                                0,
                                            )}
                                            isInCents={true}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                <div className="text-gray-500 text-sm mb-1">Lifetime Spend</div>
                                <div className="font-semibold text-lg text-gray-900">
                                    <Price price={customer.stats.lifetimeSpend!} />
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                <div className="text-gray-500 text-sm mb-1">Lifetime ARO</div>
                                <div className="font-semibold text-lg text-gray-900">
                                    <Price price={customer.stats.lifetimeARO!} />
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                <div className="text-gray-500 text-sm mb-1">Lifetime Profit</div>
                                <div className="font-semibold text-lg text-gray-900">
                                    <Price price={customer.stats.lifetimeProfit!} />
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                <div className="text-gray-500 text-sm mb-1">Lifetime ARO Profit</div>
                                <div className="font-semibold text-lg text-gray-900">
                                    <Price price={customer.stats.lifetimeAROProfit!} />
                                </div>
                            </div>

                            {!!customer.stats.lastCompletedROTotal && (
                                <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                                    <div className="text-gray-500 text-sm mb-1">Lifetime Archived RO Total</div>
                                    <div className="font-semibold text-lg text-gray-900">
                                        <Price price={customer.stats.lastCompletedROTotal} />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="text-xs text-gray-500 italic px-2">
                            * Does not include active Repair Orders.
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomerStats;
